import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { FCM } from '@capacitor-community/fcm';
import { Platform, ToastController } from '@ionic/angular';
import { createEffect } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { forkJoin, from, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { resolveCoreLanguage } from './core.i18n';
import {
  CoreActionPlatformInitialized,
  CoreActions,
  CoreActionSetAppVersion,
  CoreActionSetNotificationTap
} from './core.actions';

import {
  CoreInAppNotification,
  CoreBiometricData,
  ENVIRONMENT,
  Environment
} from './core.models';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { SharedPopupService } from '@bepatient/shared';
import { CoreUtilsService } from './core-utils';
import { BiometricService } from './biometric.service';
// import { environment } from 'apps/smart-app/src/environments/environment';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

@Injectable()
export class CoreEffectsPlatform {
  private platform = inject(Platform);
  private translateService = inject(TranslateService);
  private toastController = inject(ToastController);
  private store$ = inject(Store);
  private router = inject(Router);
  private sharedPopupService = inject(SharedPopupService);
  private coreUtilsService = inject(CoreUtilsService);
  private biometric = inject(BiometricService);
  private env = inject<Environment>(ENVIRONMENT);

  private notification: CoreInAppNotification;
  private platformResume: boolean;

  platformReady$: Observable<CoreActions> = createEffect(() =>
    from(this.platform.ready()).pipe(
      tap(() => {
        // All the native only plugin should be inside isNative check
        if (Capacitor.isNativePlatform()) {
          StatusBar.setStyle({
            style: Style.Default
          });

          // Register Push Notification and FCM
          this.coreUtilsService.registerPush();
          FCM.setAutoInit({ enabled: true });
          this.coreUtilsService.initializeDeeplinkListener();
        }
        this.coreUtilsService.userPreferenceCheck();

        // Setting session popup local storage value empty
        localStorage.setItem('sessionPopup', '');
        localStorage.setItem('isDarkTheme', '');

        // Automatically apply default/light/dark theme based on local storage
        let savedTheme = localStorage.getItem('bresearch-theme');
        if (!savedTheme) {
          savedTheme = 'system';
          localStorage.setItem('bresearch-theme', 'system');
        }
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

        // Determine the actual theme to apply
        const getActualTheme = (theme: string): string =>
          theme === 'system' ? (prefersDark.matches ? 'dark' : 'light') : theme;

        const actualTheme = getActualTheme(savedTheme);

        // Apply theme to DOM
        document.documentElement.setAttribute('data-theme', actualTheme);

        // Apply high contrast mode based on local storage and system preference
        let savedHighContrast = localStorage.getItem('bresearch-high-contrast');
        if (!savedHighContrast) {
          savedHighContrast = 'system';
          localStorage.setItem('bresearch-high-contrast', 'system');
        }
        const prefersHighContrast = window.matchMedia(
          '(prefers-contrast: more)'
        );

        // Determine if high contrast should be enabled
        const shouldEnableHighContrast =
          savedHighContrast === 'on' ||
          (savedHighContrast === 'system' && prefersHighContrast.matches);

        // Apply high contrast to DOM
        document.documentElement.classList.toggle(
          'ion-palette-high-contrast',
          shouldEnableHighContrast
        );

        // Apply saved font scale with safety checks and clamping
        const DEFAULT_FONT_SCALE = 1;
        const MIN_FONT_SCALE = 1;
        const MAX_FONT_SCALE = 1.5;
        const savedFontScale = localStorage.getItem('bresearch-font-scale');

        const parsedFontScale = savedFontScale
          ? Number.parseFloat(savedFontScale)
          : DEFAULT_FONT_SCALE;

        const sanitizedFontScale = Number.isFinite(parsedFontScale)
          ? Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, parsedFontScale))
          : DEFAULT_FONT_SCALE;

        const sanitizedFontScaleString = sanitizedFontScale.toString();

        if (savedFontScale !== sanitizedFontScaleString) {
          localStorage.setItem(
            'bresearch-font-scale',
            sanitizedFontScaleString
          );
        }

        document.documentElement.style.setProperty(
          '--global-font-scale',
          sanitizedFontScaleString
        );
        document.documentElement.setAttribute(
          'data-font-scale',
          sanitizedFontScaleString
        );

        // Android only: Apply edge-to-edge background color based on theme
        const updateEdgeToEdgeColor = (isDark: boolean) => {
          EdgeToEdge.setBackgroundColor({
            color: isDark ? '#000000' : '#ffffff'
          });
        };

        if (Capacitor.getPlatform() === 'android') {
          updateEdgeToEdgeColor(actualTheme === 'dark');
        }

        // Listen for system theme changes when system theme is selected
        prefersDark.addEventListener('change', e => {
          const currentTheme = localStorage.getItem('bresearch-theme');
          // Only apply if system theme is selected
          if (currentTheme === 'system') {
            const newSystemTheme = e.matches ? 'dark' : 'light';
            document.documentElement.setAttribute('data-theme', newSystemTheme);
            document.documentElement.classList.toggle(
              'ion-palette-dark',
              e.matches
            );
            if (Capacitor.getPlatform() === 'android') {
              updateEdgeToEdgeColor(e.matches);
            }
          }
        });

        // Listen for system high contrast changes when system preference is selected
        prefersHighContrast.addEventListener('change', e => {
          const currentHighContrast = localStorage.getItem(
            'bresearch-high-contrast'
          );
          // Only apply if system preference is selected
          if (currentHighContrast === 'system') {
            document.documentElement.classList.toggle(
              'ion-palette-high-contrast',
              e.matches
            );
          }
        });

        moment.locale('en');
        this.translateService.setDefaultLang('en');
        const browserLang = this.translateService.getBrowserLang();
        const cultureLang = this.translateService.getBrowserCultureLang();
        const resolved = resolveCoreLanguage(browserLang, cultureLang);
        moment.locale(resolved.momentLocale);
        this.translateService.use(resolved.translateLang);
        // Set <html lang> so CSS `:lang()` font fallbacks resolve from boot.
        document.documentElement.lang = resolved.translateLang;
        if (!Capacitor.isNativePlatform()) {
          // Defensively clear any enabled/enrolled biometric flags a prior
          // buggy build may have persisted from a web/webview session —
          // biometric auth can never genuinely succeed on this platform.
          const webBiometricData = this.biometric.getDeviceBiometricData();
          if (
            webBiometricData &&
            webBiometricData.enableBiometricLogin !== 'false'
          ) {
            this.biometric.disableBiometric();
          }
        }
        if (Capacitor.isNativePlatform()) {
          this.biometric
            .isBiometricAvailable()
            .then(response => {
              if (
                response.isAvailable &&
                (!this.biometric.getDeviceBiometricData() ||
                  (this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled') ||
                  (this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData().biometricEnrolled ==
                      'false'))
              ) {
                const biometricData: CoreBiometricData = {
                  biometricType: this.biometric.getBiometricType(
                    response.biometryType
                  ),
                  enableBiometricLogin:
                    this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled'
                      ? 'true'
                      : 'false',
                  keychainEnrolled:
                    this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled' &&
                    this.biometric.getDeviceBiometricData().permission == 'true'
                      ? 'true'
                      : 'false',
                  biometricEnrolled: 'true',
                  permission:
                    this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData().permission ==
                      'false' &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled'
                      ? 'true'
                      : 'false'
                };
                this.biometric.setDeviceBiometricData(biometricData);
              } else if (!response.isAvailable && response.errorCode === 3) {
                const biometricData: CoreBiometricData = {
                  biometricType: this.biometric.getBiometricType(
                    response.biometryType
                  ),
                  enableBiometricLogin:
                    response.biometryType != 0 &&
                    this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled'
                      ? 'true'
                      : 'disabled',
                  keychainEnrolled:
                    response.biometryType != 0 &&
                    this.biometric.getDeviceBiometricData() &&
                    this.biometric.getDeviceBiometricData()
                      .enableBiometricLogin == 'disabled'
                      ? 'true'
                      : 'false',
                  biometricEnrolled: 'false',
                  permission:
                    (this.biometric.getDeviceBiometricData() &&
                      this.biometric.getDeviceBiometricData().permission) ||
                    'false'
                };
                this.biometric.setDeviceBiometricData(biometricData);
              }
            })
            .catch(err => {
              // If biometric was available previously and now it is not available
              // we know biometric present so set the status disabled
              let previousBiometricData =
                this.biometric.getDeviceBiometricData();
              if (err && typeof err === 'object' && err.code == '1') {
                // Biometric not enrolled
                const biometricData: CoreBiometricData = {
                  biometricType: 'none',
                  keychainEnrolled: 'false',
                  enableBiometricLogin: 'disabled',
                  biometricEnrolled: 'false',
                  permission: 'false'
                };
                this.biometric.setDeviceBiometricData(biometricData);
              } else if (previousBiometricData) {
                const biometricData: CoreBiometricData = {
                  ...previousBiometricData,
                  enableBiometricLogin: 'disabled'
                };
                this.biometric.setDeviceBiometricData(biometricData);
              }
            });
        }
      }),
      switchMap(() =>
        forkJoin([
          from(
            Capacitor.isNativePlatform()
              ? App.getInfo().then(
                  v => v.version,
                  () => void 0
                )
              : Promise.resolve(this.env?.version || void 0)
          ).pipe(map(v => new CoreActionSetAppVersion(v)))
        ])
      ),
      switchMap(actions => [...actions, new CoreActionPlatformInitialized()])
    )
  );

  fcmNotification$: Observable<any> = createEffect(
    () =>
      from(this.platform.ready()).pipe(
        filter(() => this.platform.is('hybrid')),
        tap(() => {
          console.log('FirebaseX: Platform ready!');
          this.platformResume = true;
          // Register
          this.coreUtilsService.registerPushNotificationListener();
        })
      ),
    { dispatch: false }
  );

  changePlatformResumeStatus$: Observable<any> = createEffect(
    () =>
      from(this.platform.pause).pipe(map(() => (this.platformResume = false))),
    { dispatch: false }
  );

  platformResume$: Observable<any> = createEffect(
    () =>
      from(this.platform.resume).pipe(
        filter(() => {
          this.platformResume = true;
          return this.platform.is('hybrid') && this.platformResume;
        })
      ),
    { dispatch: false }
  );

  maintenanceModePopupCheckResume$: Observable<any> = createEffect(
    () =>
      from(this.platform.resume).pipe(
        filter(() => {
          this.platformResume = true;
          return this.platformResume;
        }),
        tap(() => {
          if (this.sharedPopupService.exist('Popup Maintenance Mode')) {
            this.sharedPopupService.dismiss('Popup Maintenance Mode');
            window.location.reload();
          }
        })
      ),
    { dispatch: false }
  );
}
